import type { DocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isObject, lowerFirst, memoize, repeat, upperFirst } from 'lodash-es';

import { useMemo } from 'react';
import { ExtendableError } from '../../../utils/errors';
import { getClient } from '../../../utils/apollo-client';
import memoizeMultiArg from '../../../utils/memoizeMultiArg';

import { SwarmType, SwarmTypeTranslations } from '../../../enums';
import { getObjectType } from '../../../utils/objectType';
import { getReturnFields } from './schema-utils';

class SwarmTypeError extends ExtendableError {}

function swarmTypeToUpperFirst(swarmType: string): string {
    return upperFirst(swarmType);
}

export function getSwarmTypeByGraphqlObject(object: { __typename?: string }): SwarmType {
    const typeName = object?.__typename;

    const foundKey = Object.keys(SwarmType).find((key) =>
        typeName!.startsWith(upperFirst(key.toLowerCase()))
    );

    if (!foundKey || !Object.keys(SwarmType).includes(foundKey)) {
        throw new SwarmTypeError(
            `Could not find swarm type for GraphQL object with type name: ${typeName}`
        );
    }

    return SwarmType[foundKey as keyof typeof SwarmType];
}

const indent = repeat(' ', 4);

function createIndents(indentLevel: number): string {
    return repeat(indent, indentLevel);
}

type FieldsObject = Record<string, unknown>;

export function convertFieldsObjectIntoGqlFieldsString(
    fields: FieldsObject,
    indentLevel: number = 0
): string {
    return Object.entries(fields)
        .map(([key, value]) => {
            const indents = createIndents(indentLevel);
            const subFields =
                isObject(value) &&
                ` {\n${convertFieldsObjectIntoGqlFieldsString(
                    value as FieldsObject,
                    indentLevel + 1
                )}\n${indents}}`;

            return `${indents}${key}${subFields || ''}`;
        })
        .join('\n');
}

const MUTATION_ERRORS = `
    errors {
        field
        message
    }
`;

const MUTATION_WARNINGS = `
    warnings {
        field
        message
    }
`;

function createFragment(name: string, type: string, fields: string): DocumentNode {
    return gql`
            fragment ${name} on ${type} {
                ${fields}
            }
        `;
}

export function createMutation(
    mutation: string,
    inputType: string,
    returnKey: string,
    fragment: DocumentNode,
    fragmentName: string,
    extraFields: string = ''
): DocumentNode {
    return gql`
        mutation ${mutation} ($input: ${inputType}!) {
            ${mutation}(input: $input) {
                ${returnKey} {
                    ...${fragmentName}
                }
                ${MUTATION_ERRORS}
                ${extraFields}
            }
        }
        ${fragment}
    `;
}

export const updateMeasuringPointPrivateNotes = gql`
    mutation updateMeasuringPointPrivateNotes($id: ID!, $privateNotes: String!) {
        updateMeasuringPointPrivateNotes(id: $id, privateNotes: $privateNotes) {
            measuringPoint {
                id
                privateNotes
                __typename
            }
        }
    }
`;

export const updateProjectPrivateNotes = gql`
    mutation updateProjectPrivateNotes($id: ID!, $privateNotes: String!) {
        updateProjectPrivateNotes(id: $id, privateNotes: $privateNotes) {
            project {
                id
                privateNotes
                __typename
            }
        }
    }
`;

export function createObjectNames(
    swarmType: string,
    isProject: boolean
): { objectName: string; objectNameLowerFirst: string } {
    const objectName = `${swarmTypeToUpperFirst(swarmType)}${getObjectType(isProject).objectName}`;

    return {
        objectName,
        objectNameLowerFirst: lowerFirst(objectName),
    };
}

export const createGetAllQueryForVariant = memoizeMultiArg(
    (swarmType: string, isProject: boolean, archived: boolean) => {
        const resolverName = `${getObjectType(isProject).objectNameLowerFirst}s`;
        const objectName = `${swarmTypeToUpperFirst(swarmType)}${
            getObjectType(isProject).objectName
        }`;
        return [
            gql`
            query get${upperFirst(resolverName)}For${upperFirst(swarmType)} {
                ${resolverName} (swarmType: ${swarmType}, archived: ${archived}) {
                    id
                    name
                    ... on ${objectName} {
                        permission
                    }
                }
            }
        `,
            resolverName,
        ];
    }
);

interface GetQueryForObjectTypeResult {
    GET_QUERY: DocumentNode;
    getResolverName: string;
}

export const createGetQueryForObjectType = memoizeMultiArg(
    (isProject: boolean): GetQueryForObjectTypeResult => {
        // Eg: project or measuringPoint
        const objectType = getObjectType(isProject).objectNameLowerFirst;

        const getResolverName = `${objectType}ById`;

        const resolvers = Object.values(SwarmType).map((swarmType) => {
            const returnFields = getReturnFields(swarmType, isProject);
            const { objectName } = createObjectNames(swarmType, isProject);

            const fragmentType = `${objectName}`;
            const fragmentName = `${objectName}Fields`;
            const fragmentFields = convertFieldsObjectIntoGqlFieldsString(returnFields);

            const FRAGMENT = createFragment(fragmentName, fragmentType, fragmentFields);

            return [FRAGMENT, fragmentName] as const;
        });

        return {
            GET_QUERY: gql`
            query ${getResolverName} ($id: ID!) {
                ${getResolverName}(id: $id) {
                    ${resolvers.map(([_, fragmentName]) => `...${fragmentName}`).join('\n')}
                }
            }
            ${resolvers.map(([FRAGMENT, _]) => FRAGMENT.loc?.source.body).join('\n')}
        `,
            getResolverName,
        };
    }
);

interface CRUQueriesForVariantResult {
    objectName: string;
    returnFieldsFragment: DocumentNode;
    returnFieldsFragmentName: string;
    GET: DocumentNode;
    getResolverName: string;
    UPDATE: DocumentNode;
    updateMutationName: string;
    CREATE: DocumentNode;
    createMutationName: string;
}

function createCRUQueriesForVariant(
    swarmType: SwarmType,
    isProject: boolean
): CRUQueriesForVariantResult {
    const returnFields = getReturnFields(swarmType, isProject);

    const { objectName, objectNameLowerFirst } = createObjectNames(swarmType, isProject);

    const fragmentType = `${objectName}`;
    const fragmentName = `${objectName}Fields`;
    const fragmentFields = convertFieldsObjectIntoGqlFieldsString(returnFields);

    const createMutationName = `${objectNameLowerFirst}Create`;
    const createMutationInput = `${objectName}CreateInput`;
    const createMutationReturnKey = `${objectNameLowerFirst}`;

    const updateMutationName = `${objectNameLowerFirst}Update`;
    const updateMutationInput = `${objectName}UpdateInput`;
    const updateMutationReturnKey = `${objectNameLowerFirst}`;

    const FRAGMENT = createFragment(fragmentName, fragmentType, fragmentFields);

    const { GET_QUERY, getResolverName } = createGetQueryForObjectType(isProject);

    return {
        objectName: objectNameLowerFirst,
        returnFieldsFragment: FRAGMENT,
        returnFieldsFragmentName: fragmentName,
        GET: GET_QUERY,
        getResolverName,
        UPDATE: createMutation(
            updateMutationName,
            updateMutationInput,
            updateMutationReturnKey,
            FRAGMENT,
            fragmentName,
            MUTATION_WARNINGS
        ),
        updateMutationName,
        CREATE: createMutation(
            createMutationName,
            createMutationInput,
            createMutationReturnKey,
            FRAGMENT,
            fragmentName,
            MUTATION_WARNINGS
        ),
        createMutationName,
    };
}

interface SwarmTypeQueries {
    objectName: string;
    returnFieldsFragment: DocumentNode;
    returnFieldsFragmentName: string;
    getEntityQuery: DocumentNode;
    getResolverName: string;
    updateEntityQuery: DocumentNode;
    updateEntityQueryName: string;
    updatePrivateNotesQuery: DocumentNode;
    updatePrivateNotesResponseKey: string;
    updatePrivateNotesEntityKey: string;
    createEntityQuery: DocumentNode;
    createEntityQueryName: string;
    getParentEntityQuery: DocumentNode | null;
    getParentResolverName: string | null;
    parentEntityFieldName: string | null;
}

export interface QueriesForSwarmType {
    false: SwarmTypeQueries;
    true: SwarmTypeQueries;
}

export const createQueriesForSwarmType = memoize((swarmType: SwarmType): QueriesForSwarmType => {
    const MP = createCRUQueriesForVariant(swarmType, false);
    const PROJECT = createCRUQueriesForVariant(swarmType, true);
    return {
        // isProject: false.
        false: {
            objectName: MP.objectName,
            returnFieldsFragment: MP.returnFieldsFragment,
            returnFieldsFragmentName: MP.returnFieldsFragmentName,
            getEntityQuery: MP.GET,
            getResolverName: MP.getResolverName,
            updateEntityQuery: MP.UPDATE,
            updateEntityQueryName: MP.updateMutationName,
            updatePrivateNotesQuery: updateMeasuringPointPrivateNotes,
            updatePrivateNotesResponseKey: 'updateMeasuringPointPrivateNotes',
            updatePrivateNotesEntityKey: 'measuringPoint',
            createEntityQuery: MP.CREATE,
            createEntityQueryName: MP.createMutationName,
            getParentEntityQuery: PROJECT.GET,
            getParentResolverName: PROJECT.getResolverName,
            parentEntityFieldName: 'project',
        },
        // isProject: true.
        true: {
            objectName: PROJECT.objectName,
            returnFieldsFragment: PROJECT.returnFieldsFragment,
            returnFieldsFragmentName: PROJECT.returnFieldsFragmentName,
            getEntityQuery: PROJECT.GET,
            getResolverName: PROJECT.getResolverName,
            updateEntityQuery: PROJECT.UPDATE,
            updateEntityQueryName: PROJECT.updateMutationName,
            updatePrivateNotesQuery: updateProjectPrivateNotes,
            updatePrivateNotesResponseKey: 'updateProjectPrivateNotes',
            updatePrivateNotesEntityKey: 'project',
            createEntityQuery: PROJECT.CREATE,
            createEntityQueryName: PROJECT.createMutationName,
            getParentEntityQuery: null,
            getParentResolverName: null,
            parentEntityFieldName: null,
        },
    };
});

const GET_MEASURING_POINT_OR_PROJECT_TYPE_BY_ID = gql`
    query GetEntityTypeById($id: ID!, $isProject: Boolean!) {
        measuringPointById(id: $id) @skip(if: $isProject) {
            ... on MeasuringPointInterface {
                name
            }
            ... on SoundMeasuringPoint {
                timezone
            }
            ... on VibrationMeasuringPoint {
                timezone
            }
            ... on AirMeasuringPoint {
                timezone
            }
            __typename
        }
        projectById(id: $id) @include(if: $isProject) {
            ... on ProjectInterface {
                name
            }
            __typename
        }
    }
`;

interface MeasuringPointResult {
    name: string;
    timezone?: string;
    __typename: string;
}

interface ProjectResult {
    name: string;
    __typename: string;
}

export async function getSwarmInfoById(
    id: string,
    isProject: boolean
): Promise<MeasuringPointResult | ProjectResult | null> {
    const { data } = await getClient().query<{
        measuringPointById: MeasuringPointResult | null;
        projectById: ProjectResult | null;
    }>({
        query: GET_MEASURING_POINT_OR_PROJECT_TYPE_BY_ID,
        variables: {
            id,
            isProject,
        },
    });

    const returnedObject = isProject ? data?.projectById : data?.measuringPointById;

    // Return null if the measuring point or project was not found.
    if (!returnedObject) {
        return null;
    }

    return returnedObject;
}

export function usePageTitle(swarmType: SwarmType, isProject: boolean, isEdit: boolean): string {
    return useMemo(() => {
        const objectType = getObjectType(isProject);
        const baseText = isEdit ? gettext('PAGE_TITLE_EDIT') : gettext('PAGE_TITLE_CREATE');

        return interpolate(
            baseText,
            {
                swarmType: SwarmTypeTranslations[swarmType],
                configType: objectType.translation,
            },
            true
        );
    }, [swarmType, isProject, isEdit]);
}

export function createEditUrl(id: string, swarmType: string, isProject: boolean): string {
    const prefix = isProject ? 'project' : 'measuring_point';
    return `/${prefix}/edit/${swarmType}/${id}`;
}

export function createMeasuringPointInProjectUrl(swarmType: string, projectId: string): string {
    return `/measuring_point/create/${swarmType}?${new URLSearchParams({ project: projectId })}`;
}
